import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { mutations, queries } from "./factories";
import {
  BookingProvidersParams,
  CancelReasonType,
  CancelVisitData,
  EditVisitCancellationData,
} from "./types";

export const useBookingProvidersQuery = (params: BookingProvidersParams) => {
  return useQuery(queries.bookingProviders(params));
};

export const useListCancelReasonsQuery = (type?: CancelReasonType) => {
  return useQuery(queries.listCancelReasons(type));
};

/**
 * Never fetches on its own by default. This endpoint reads the visit history from the EHR
 * live, so it sits behind the "Load visits" button and its cooldown, and every caller shares
 * the one cache entry that button fills.
 *
 * `fetchIfMissing` is for callers that can be reached without passing through that button, a
 * pasted URL or a refresh, where nothing else would ever populate the cache. It fetches only
 * when the cache is empty, so a caller arriving with a warm cache still costs zero EHR calls.
 */
export const useAppointmentsListQuery = (
  userId?: string,
  { fetchIfMissing = false }: { fetchIfMissing?: boolean } = {},
) => {
  const queryClient = useQueryClient();
  const query = queries.appointments(userId ?? "");

  const enabled =
    fetchIfMissing &&
    Boolean(userId) &&
    queryClient.getQueryData(query.queryKey) === undefined;

  return useQuery({ ...query, enabled });
};

export const useCancelVisitMutation = () => {
  return useMutation({
    mutationFn: async ({
      cancelVisitData,
    }: {
      cancelVisitData: CancelVisitData;
    }) => {
      return await mutations.cancelVisit(cancelVisitData);
    },
  });
};

export const useEditVisitCancellationMutation = () => {
  return useMutation({
    mutationFn: (data: EditVisitCancellationData) =>
      mutations.editVisitCancellation(data),
  });
};

export const useSyncEmailEnteredToPauboxMutation = () => {
  return useMutation({
    mutationFn: (data: {
      email: string;
      stateId: number;
      insuranceId: number;
    }) => mutations.syncEmailEnteredToPaubox(data),
  });
};
